// RecommendFormatsToolUI — branded tool-UI for the agent's
// `recommendTrainingFormats` tool call, rendered inline in the Aidea Assistant
// chat thread (instead of assistant-ui's generic tool rendering).
//
// Registration: assistant-ui's `makeAssistantToolUI({ toolName, render })`
// returns a React component that, while mounted inside the
// <AssistantRuntimeProvider>, registers `render` as the renderer for every
// tool-call message part whose `toolName` matches. We render
// <RecommendFormatsToolUI /> once in ChatPanel (alongside MessageCapture).
//
// Render-prop shape (verified against the installed @assistant-ui/react →
// @assistant-ui/core ToolCallMessagePartProps): the render component receives
//   { args, result, status, toolName, toolCallId, isError, argsText, ... }
// where
//   - args:   the tool args object `{ topic, audience? }` (PARTIAL while the
//             model is still streaming the call).
//   - result: TResult | undefined — here the backend returns a JSON STRING
//             `{"formats":[{format,why}, …]}`, so we parse it defensively
//             (it may also arrive already-parsed depending on transport).
//   - status: a discriminated union with `status.type` of
//             'running' | 'complete' | 'incomplete' | 'requires-action'.
//
// Styling follows the chat scoping contract: every utility is `tw-`-prefixed and
// only takes effect under `.aidea-chat-root` (tool UIs render inside the Thread,
// which is inside that root). Brand tokens via CSS vars (`--aidea-primary`, …).
// Vocabulary mirrors PreviewCard.jsx. No emoji.

import React from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { RecommendIcon, SpinnerIcon } from "../icons";

// Parse the tool result into an array of `{ format, why }`. The backend sends a
// JSON string; some transports may hand it back already parsed. Be defensive on
// both axes (string-vs-object, and a few plausible shapes) and never throw.
function parseFormats(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  // Accept `{ formats: [...] }` (the documented shape) or a bare array.
  const formats = Array.isArray(data) ? data : data && data.formats;
  if (!Array.isArray(formats)) return null;
  // Keep only well-formed items; tolerate a missing `why`.
  const cleaned = formats
    .filter((f) => f && typeof f.format === "string" && f.format.trim())
    .map((f) => ({
      format: f.format.trim(),
      why: typeof f.why === "string" ? f.why.trim() : "",
    }));
  return cleaned.length ? cleaned : null;
}

// Small violet-tint header chip, e.g. "Recommended formats".
function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <RecommendIcon size={12} />
      {label}
    </span>
  );
}

// Card chrome shared by every state so the tool always looks like one card.
function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[440px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

// Running state: a small "Recommending formats for '<topic>'…" line + spinner.
// `args.topic` may not have streamed yet — fall back to a generic label.
function RunningState({ topic }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          {topic ? (
            <>
              Recommending formats for{" "}
              <span className="tw-font-medium tw-text-[var(--aidea-fg)]">“{topic}”</span>…
            </>
          ) : (
            "Recommending training formats…"
          )}
        </span>
      </div>
    </Card>
  );
}

// Empty / parse-failure fallback — still a branded card so it reads as the same
// tool, just with nothing to show.
function EmptyState({ topic }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Recommended formats" />
        {topic && (
          <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-font-semibold tw-text-[var(--aidea-fg)]">
            {topic}
          </span>
        )}
      </div>
      <div className="tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        No recommendations.
      </div>
    </Card>
  );
}

// Complete state: header chip + topic, then the list of formats.
function ResultState({ topic, audience, formats }) {
  return (
    <Card>
      {/* Header: chip + topic */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Recommended formats" />
        {topic && (
          <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-font-semibold tw-text-[var(--aidea-fg)]">
            {topic}
          </span>
        )}
      </div>

      {audience && (
        <p className="tw-m-0 tw-px-3 tw-pt-1 tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
          For {audience}
        </p>
      )}

      {/* Body: one row per recommended format. */}
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-px-3 tw-py-2.5">
        {formats.map((f, i) => (
          <li
            key={`${f.format}-${i}`}
            className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-px-2.5 tw-py-2"
          >
            <span
              aria-hidden="true"
              className="tw-mt-0.5 tw-flex tw-h-4 tw-w-4 tw-flex-none tw-items-center tw-justify-center tw-text-[var(--aidea-primary)]"
            >
              <RecommendIcon size={14} />
            </span>
            <span className="tw-min-w-0">
              <span className="tw-block tw-text-[13px] tw-font-semibold tw-leading-snug tw-text-[var(--aidea-fg)]">
                {f.format}
              </span>
              {f.why && (
                <span className="tw-block tw-text-[12px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
                  {f.why}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export const RecommendFormatsToolUI = makeAssistantToolUI({
  toolName: "recommendTrainingFormats",
  render: ({ args, result, status }) => {
    const topic = args && typeof args.topic === "string" ? args.topic : "";
    const audience = args && typeof args.audience === "string" ? args.audience : "";

    // While the tool call is still running (model streaming args, or the tool
    // executing) show the loading affordance. `status.type` is the discriminated
    // union from ToolCallMessagePartStatus.
    if (status && status.type === "running") {
      return <RunningState topic={topic} />;
    }

    const formats = parseFormats(result);
    if (!formats) {
      // Completed (or incomplete) with nothing parseable — graceful fallback.
      return <EmptyState topic={topic} />;
    }

    return <ResultState topic={topic} audience={audience} formats={formats} />;
  },
});

export default RecommendFormatsToolUI;
